import type { Document } from "../document/Document";
import type { Selection } from "../selection/Selection";
import type { Command } from "./Command";

// Replace the document's active pixel selection with a new one (or clear it).
// Tools that build a selection interactively (Marquee, Lasso, Magic Wand) emit
// SetSelectionCommand at pointer-up for the undo entry, and emitTransient
// during drag for the live preview.
//
// The command stores only the *next* selection; the inverse captures the
// previous selection from `before` at invert-time, so re-applying the chain
// stays correct under history compaction.

export class SetSelectionCommand implements Command {
  readonly kind = "set-selection";
  readonly transient: boolean;
  // `transient` was previously hardcoded true, so a COMMITTED selection (emitted
  // via ctx.emit → History.execute at pointer-up / from the Select menu) was
  // applied but never recorded — selections could not be undone/redone and did
  // not clear the redo stack (audit i43). It now defaults to false (committed,
  // undoable). Live drag previews use ctx.emitTransient → applyTransient, which
  // bypasses History entirely, so they still create no undo entry.
  constructor(private readonly next: Selection | null, transient = false) {
    this.transient = transient;
  }

  apply(doc: Document): Document {
    return { ...doc, selection: this.next };
  }

  invert(before: Document, _after: Document): Command {
    return new SetSelectionCommand(before.selection);
  }
}
